import React, { useState, useContext } from 'react';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import Chatbot from '@/components/shared/Chatbot';
import { Button } from '@/components/ui/button';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import { 
  Award, Users, TrendingUp, CheckCircle, Star, GraduationCap, Briefcase, 
  BookOpen, Building2, Phone, MessageCircle, ChevronDown, ChevronUp,
  Clock, IndianRupee, Shield, Target, Sparkles, ArrowRight, Zap,
  BadgeCheck, MapPin, Calendar, Play, Eye
} from 'lucide-react';

const ProgramsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useContext(AuthContext);
  const [expandedProgram, setExpandedProgram] = useState(null);

  // Handle Apply Now click
  const handleApplyClick = (program) => {
    if (isAuthenticated && user) {
      if (user.role === 'student') {
        toast.success(`Applying for ${program?.title || 'program'}...`);
        navigate('/student/dashboard?tab=courses');
      } else {
        toast.error('Only students can apply for programs');
      }
    } else {
      toast.info('Please login to apply for programs');
      navigate('/auth/login?redirect=/programs');
    }
  };

  // Impact Stats
  const impactStats = [
    { number: '25,000+', label: 'Candidates Trained', icon: Users, color: 'blue' },
    { number: '85%', label: 'Placement Rate', icon: TrendingUp, color: 'emerald' },
    { number: '100+', label: 'Batches Completed', icon: Award, color: 'purple' },
    { number: '8+', label: 'Program Categories', icon: Star, color: 'orange' }
  ];

  // Government Skill Programs
  const governmentPrograms = [
    {
      id: 'pmkvy',
      title: 'PMKVY',
      fullName: 'Pradhan Mantri Kaushal Vikas Yojana',
      badge: 'Govt Scheme',
      badgeColor: 'emerald',
      icon: Building2,
      versions: '1.0, 2.0, 3.0 & 4.0',
      shortDesc: 'India\'s largest skill certification scheme by Ministry of Skill Development',
      trained: '15,000+',
      placement: '85%',
      benefits: [
        'Free skill training',
        'Government certificate',
        'Placement support',
        'Stipend during training'
      ],
      fullDescription: 'PMKVY is the flagship scheme of the Ministry of Skill Development & Entrepreneurship (MSDE). The objective of this skill certification scheme is to enable a large number of Indian youth to take up industry-relevant skill training.',
      achievements: [
        'Successfully trained 15,000+ candidates across PMKVY 1.0 to 4.0',
        'Achieved 85%+ placement rate',
        'Recognized as Star Performing Training Partner by NSDC',
        'Trained candidates in 25+ job roles'
      ],
      sectors: ['Retail', 'IT-ITeS', 'Electronics', 'Construction', 'Healthcare', 'Tourism'],
      careers: ['Sales Associate', 'Data Entry Operator', 'Electronics Technician', 'Healthcare Assistant'],
      salaryRange: '₹12,000 - ₹25,000/month'
    },
    {
      id: 'nulm',
      title: 'NULM',
      fullName: 'National Urban Livelihoods Mission',
      badge: 'Govt Scheme',
      badgeColor: 'emerald',
      icon: Users,
      shortDesc: 'Skill training for urban poor with self-employment support',
      trained: '3,000+',
      placement: '78%',
      benefits: [
        'Free training for urban poor',
        'Self-employment support',
        'Credit linkage & subsidy',
        'Market linkage support'
      ],
      fullDescription: 'NULM is a Central government program aimed at reducing poverty and vulnerability of urban poor households by enabling them to access gainful self-employment and skilled wage employment opportunities.',
      achievements: [
        'Trained 3,000+ candidates from urban poor backgrounds',
        'Facilitated self-employment for 500+ trained candidates',
        'Successfully completed 50+ training batches',
        'Achieved 78% success rate'
      ],
      sectors: ['Tailoring', 'Beauty & Wellness', 'Electrical Work', 'Mobile Repairing', 'Computer Operations'],
      careers: ['Tailor', 'Beautician', 'Electrician', 'Mobile Technician'],
      salaryRange: '₹10,000 - ₹20,000/month'
    },
    {
      id: 'esdm',
      title: 'ESDM',
      fullName: 'Electronics System Design & Manufacturing',
      badge: 'Govt Scheme',
      badgeColor: 'emerald',
      icon: Zap,
      shortDesc: 'Electronics sector skill development under MeitY',
      trained: '1,500+',
      placement: '70%',
      benefits: [
        'Industry-aligned curriculum',
        'Hands-on lab training',
        'Direct company placement',
        'ESSCI certification'
      ],
      fullDescription: 'ESDM is a sector-specific program in collaboration with ESSCI under MeitY for electronics sector skill development. It focuses on building skilled workforce for the growing electronics manufacturing industry.',
      achievements: [
        'Trained 1,500+ candidates in ESDM sector',
        'Partnered with leading electronics companies',
        'Established state-of-the-art electronics labs',
        '70% placement in electronics manufacturing units'
      ],
      sectors: ['PCB Assembly', 'Mobile Phone Repair', 'LED Manufacturing', 'Consumer Electronics', 'IoT Devices'],
      careers: ['PCB Technician', 'Mobile Repair Technician', 'LED Assembler', 'IoT Technician'],
      salaryRange: '₹15,000 - ₹30,000/month'
    }
  ];

  // Apprenticeship Programs
  const apprenticeshipPrograms = [
    {
      id: 'naps',
      title: 'NAPS',
      fullName: 'National Apprenticeship Promotion Scheme',
      badge: 'Apprenticeship',
      badgeColor: 'blue',
      icon: Briefcase,
      shortDesc: 'Earn while you learn with industry apprenticeship',
      trained: '500+',
      placement: '90%',
      stipend: '₹5,000 - ₹9,000/month',
      benefits: [
        'On-the-job training',
        'Monthly stipend',
        'Industry exposure',
        'National certificate'
      ],
      fullDescription: 'NAPS is a scheme under MSDE promoting apprenticeship training and enhancing employability of youth by providing practical skills in real work environment.',
      achievements: [
        'Engaged 500+ apprentices with leading companies',
        'Successfully completed 25+ apprenticeship batches',
        'Achieved 90%+ retention rate post-apprenticeship',
        'Partner with 30+ establishments'
      ],
      sectors: ['Manufacturing', 'Services', 'IT Sector', 'Retail', 'Engineering'],
      careers: ['Junior Technician', 'Office Assistant', 'Sales Executive', 'Machine Operator'],
      salaryRange: '₹15,000 - ₹35,000/month (post apprenticeship)'
    },
    {
      id: 'nats',
      title: 'NATS',
      fullName: 'National Apprenticeship Training Scheme',
      badge: 'Apprenticeship',
      badgeColor: 'blue',
      icon: GraduationCap,
      shortDesc: 'For Graduates & Diploma holders - 1 year industry training',
      trained: '300+',
      placement: '85%',
      stipend: '₹9,000 - ₹12,000/month',
      benefits: [
        '1-year practical training',
        'Monthly stipend (₹9,000-12,000)',
        'MHRD certificate',
        'Priority hiring'
      ],
      fullDescription: 'NATS is a graduate and diploma holder apprenticeship scheme under MHRD for technical graduates to gain practical training in industries.',
      achievements: [
        'Placed 300+ engineering graduates as apprentices',
        'Partnerships with leading manufacturing companies',
        'Successfully trained diploma holders in core industries',
        '85%+ conversion rate to permanent employment'
      ],
      eligibility: ['Diploma holders (any discipline)', 'Graduate degree holders', 'B.Tech/B.E. students'],
      sectors: ['Engineering', 'IT & Software', 'Manufacturing', 'Automobile', 'Infrastructure'],
      careers: ['Graduate Engineer Trainee', 'Junior Engineer', 'Technical Associate', 'IT Support'],
      salaryRange: '₹20,000 - ₹45,000/month (post apprenticeship)'
    }
  ];

  // Vocational Programs
  const vocationalPrograms = [
    {
      id: 'dvoc',
      title: 'D.Voc',
      fullName: 'Diploma in Vocation',
      badge: 'NSQF Level 5',
      badgeColor: 'purple',
      icon: Award,
      duration: '1 Year',
      shortDesc: 'UGC approved diploma with 70% practical training',
      benefits: [
        'NSQF Level 5 certification',
        'Industry internship',
        'Credit transfer to B.Voc',
        'Govt & private sector recognition'
      ],
      fullDescription: 'D.Voc is a UGC approved diploma program aligned with NSQF providing industry-relevant vocational education with 70% practical training.',
      courses: [
        { name: 'Retail Management', fee: '₹25,000/year' },
        { name: 'IT & Software', fee: '₹30,000/year' },
        { name: 'Banking & Insurance', fee: '₹28,000/year' },
        { name: 'Healthcare', fee: '₹35,000/year' }
      ],
      careers: ['Store Manager Trainee', 'IT Support', 'Bank Assistant', 'Healthcare Assistant'],
      salaryRange: '₹15,000 - ₹30,000/month'
    },
    {
      id: 'bvoc',
      title: 'B.Voc',
      fullName: 'Bachelor of Vocation',
      badge: 'NSQF Level 7 | UGC',
      badgeColor: 'purple',
      icon: GraduationCap,
      duration: '3 Years',
      shortDesc: 'Degree program with multiple exit options',
      benefits: [
        'NSQF Level 7 certification',
        '70% practical curriculum',
        'Multiple exit options',
        'Dual certification'
      ],
      fullDescription: 'B.Voc is a UGC approved 3-year degree program with skill-based education and NSQF certification. It offers multiple exit points allowing flexibility in career choices.',
      courses: [
        { name: 'Software Development', fee: '₹45,000/year' },
        { name: 'Banking & Finance', fee: '₹40,000/year' },
        { name: 'Retail Management', fee: '₹38,000/year' },
        { name: 'Tourism & Hospitality', fee: '₹42,000/year' }
      ],
      exitOptions: [
        { year: 'Year 1', cert: 'Diploma (NSQF 5)' },
        { year: 'Year 2', cert: 'Advanced Diploma (NSQF 6)' },
        { year: 'Year 3', cert: 'B.Voc Degree (NSQF 7)' }
      ],
      careers: ['Software Developer', 'Bank Officer', 'Retail Manager', 'Hotel Manager'],
      salaryRange: '₹20,000 - ₹50,000/month'
    }
  ];

  // Degree Programs
  const degreePrograms = [
    {
      id: 'degree',
      title: 'Degree Programs',
      fullName: 'Distance Education - UGC Recognized',
      badge: 'UGC Recognized',
      badgeColor: 'indigo',
      icon: BookOpen,
      shortDesc: 'Flexible learning for working professionals',
      enrolled: '1,000+',
      benefits: [
        'Study while you work',
        'UGC recognized degrees',
        'Affordable fees',
        'Online study support'
      ],
      fullDescription: 'Distance education degree programs in collaboration with recognized universities for working professionals and students.',
      categories: [
        {
          name: 'Diploma',
          color: 'purple',
          courses: [
            { name: 'PGDCA', duration: '1 Year', fee: '₹26,000 Total' }
          ]
        },
        {
          name: 'Undergraduate',
          color: 'blue',
          courses: [
            { name: 'BCA', duration: '3 Years', fee: '₹21,500/year' },
            { name: 'BBA', duration: '3 Years', fee: '₹21,500/year' },
            { name: 'B.Com', duration: '3 Years', fee: '₹21,000/year' },
            { name: 'B.A.', duration: '3 Years', fee: '₹16,000/year' }
          ]
        },
        {
          name: 'Postgraduate',
          color: 'emerald',
          courses: [
            { name: 'MBA', duration: '2 Years', fee: '₹35,000/year' },
            { name: 'MCA', duration: '2 Years', fee: '₹35,000/year' },
            { name: 'M.Com', duration: '2 Years', fee: '₹21,000/year' },
            { name: 'M.Sc (Maths)', duration: '2 Years', fee: '₹25,000/year' }
          ]
        }
      ],
      careers: ['IT Professional', 'Business Manager', 'Accountant', 'Teacher'],
      salaryRange: '₹25,000 - ₹60,000/month'
    }
  ];

  // All programs combined for display
  const allPrograms = [...governmentPrograms, ...apprenticeshipPrograms, ...vocationalPrograms, ...degreePrograms];

  // Why Choose Section
  const whyChoose = [
    { icon: Shield, title: 'Government Certification', desc: 'All programs certified by Skill India, NSDC, and relevant authorities' },
    { icon: IndianRupee, title: 'Free Training Options', desc: 'Multiple government-funded free training programs available' },
    { icon: Building2, title: 'Industry Exposure', desc: 'Real-world training with industry partnerships and internships' },
    { icon: Briefcase, title: 'Placement Support', desc: '85%+ placement rate with dedicated career assistance' }
  ];

  // Career Outcomes
  const careerOutcomes = [
    { program: 'PMKVY', role: 'Skilled Worker', salary: '₹12,000 - ₹25,000/month' },
    { program: 'NAPS/NATS', role: 'Apprentice → Employee', salary: '₹15,000 - ₹45,000/month' },
    { program: 'D.Voc/B.Voc', role: 'Skilled Professional', salary: '₹20,000 - ₹50,000/month' },
    { program: 'Degree', role: 'Graduate Professional', salary: '₹25,000 - ₹60,000/month' }
  ];

  // Program Card Component
  const ProgramCard = ({ program }) => {
    const isExpanded = expandedProgram === program.id;
    const Icon = program.icon;

    return (
      <div 
        className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
        data-testid={`program-card-${program.id}`}
      >
        {/* Card Header */}
        <div className={`bg-gradient-to-r ${
          program.badgeColor === 'emerald' ? 'from-emerald-600 to-teal-600' :
          program.badgeColor === 'blue' ? 'from-blue-600 to-indigo-600' :
          program.badgeColor === 'purple' ? 'from-purple-600 to-pink-600' :
          'from-indigo-600 to-purple-600'
        } p-6`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                <Icon className="w-7 h-7 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-2xl font-bold text-white">{program.title}</h3>
                  <span className="bg-white/20 text-white text-xs px-2 py-1 rounded-full font-medium">
                    {program.badge}
                  </span>
                </div>
                <p className="text-white/80 text-sm">{program.fullName}</p>
              </div>
            </div>
          </div>
          {program.duration && (
            <div className="mt-4 flex items-center gap-4 text-white/90 text-sm">
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {program.duration}</span>
              {program.trained && <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {program.trained} Trained</span>}
            </div>
          )}
        </div>

        {/* Card Body */}
        <div className="p-6">
          <p className="text-slate-600 mb-4">{program.shortDesc}</p>

          {/* Quick Stats */}
          {(program.trained || program.placement || program.stipend) && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              {program.trained && (
                <div className="text-center p-3 bg-slate-50 rounded-xl">
                  <p className="text-xl font-bold text-blue-600">{program.trained}</p>
                  <p className="text-xs text-slate-500">Trained</p>
                </div>
              )}
              {program.placement && (
                <div className="text-center p-3 bg-slate-50 rounded-xl">
                  <p className="text-xl font-bold text-emerald-600">{program.placement}</p>
                  <p className="text-xs text-slate-500">Placement</p>
                </div>
              )}
              {program.stipend && (
                <div className="text-center p-3 bg-slate-50 rounded-xl">
                  <p className="text-lg font-bold text-purple-600">{program.stipend.split('/')[0]}</p>
                  <p className="text-xs text-slate-500">Stipend</p>
                </div>
              )}
            </div>
          )}

          {/* Benefits */}
          <div className="mb-6">
            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" /> Key Benefits
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {program.benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                  <BadgeCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Expand/Collapse Button */}
          <button
            onClick={() => setExpandedProgram(isExpanded ? null : program.id)}
            className="w-full flex items-center justify-center gap-2 py-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            {isExpanded ? (
              <>Hide Details <ChevronUp className="w-4 h-4" /></>
            ) : (
              <>View Full Details <ChevronDown className="w-4 h-4" /></>
            )}
          </button>

          {/* Expanded Content */}
          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-slate-200 space-y-6 animate-in slide-in-from-top-2">
              {/* Full Description */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">About This Program</h4>
                <p className="text-slate-600 text-sm">{program.fullDescription}</p>
              </div>

              {/* Achievements */}
              {program.achievements && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Award className="w-4 h-4 text-amber-500" /> Our Achievements
                  </h4>
                  <ul className="space-y-2">
                    {program.achievements.map((achievement, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        {achievement}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Courses for Vocational/Degree */}
              {program.courses && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Available Courses</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {program.courses.map((course, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-3 text-sm">
                        <p className="font-medium text-slate-900">{course.name}</p>
                        <p className="text-emerald-600">{course.fee}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Degree Categories */}
              {program.categories && (
                <div className="space-y-4">
                  {program.categories.map((cat, idx) => (
                    <div key={idx}>
                      <h5 className={`font-medium text-${cat.color}-700 mb-2`}>{cat.name}</h5>
                      <div className="grid grid-cols-2 gap-2">
                        {cat.courses.map((course, cIdx) => (
                          <div key={cIdx} className="bg-slate-50 rounded-lg p-3 text-sm">
                            <p className="font-medium text-slate-900">{course.name}</p>
                            <p className="text-slate-500 text-xs">{course.duration}</p>
                            <p className="text-emerald-600">{course.fee}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Exit Options for B.Voc */}
              {program.exitOptions && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Multiple Exit Options</h4>
                  <div className="flex gap-4">
                    {program.exitOptions.map((opt, idx) => (
                      <div key={idx} className="flex-1 text-center p-3 bg-emerald-50 rounded-xl">
                        <div className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center mx-auto mb-2 text-sm font-bold">
                          {idx + 1}
                        </div>
                        <p className="text-sm font-medium text-emerald-800">{opt.year}</p>
                        <p className="text-xs text-emerald-600">{opt.cert}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Eligibility for NATS */}
              {program.eligibility && (
                <div className="bg-amber-50 rounded-xl p-4">
                  <h4 className="font-semibold text-amber-800 mb-2">Eligibility</h4>
                  <ul className="space-y-1">
                    {program.eligibility.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-amber-700">
                        <CheckCircle className="w-4 h-4" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Sectors */}
              {program.sectors && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Training Sectors</h4>
                  <div className="flex flex-wrap gap-2">
                    {program.sectors.map((sector, idx) => (
                      <span key={idx} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                        {sector}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Career & Salary */}
              {program.careers && (
                <div className="bg-slate-900 rounded-xl p-4">
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Career Opportunities
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {program.careers.map((career, idx) => (
                      <span key={idx} className="bg-white/10 text-white px-3 py-1 rounded-full text-sm">
                        {career}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-white/20">
                    <span className="text-slate-400 text-sm">Expected Salary</span>
                    <span className="text-emerald-400 font-bold">{program.salaryRange}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6">
            {/* Show Apply Now only for D.Voc, B.Voc, and Degree Programs */}
            {['dvoc', 'bvoc', 'degree'].includes(program.id) ? (
              <Button 
                onClick={() => handleApplyClick(program)} 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <GraduationCap className="w-4 h-4 mr-2" /> Apply Now
              </Button>
            ) : (
              <Button 
                onClick={() => navigate('/contact')} 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                <Phone className="w-4 h-4 mr-2" /> Contact Us
              </Button>
            )}
            <Button 
              variant="outline"
              className="flex-1 border-slate-300"
              onClick={() => setExpandedProgram(isExpanded ? null : program.id)}
            >
              <Eye className="w-4 h-4 mr-2" /> {isExpanded ? 'Less' : 'Details'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* HERO SECTION */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 py-16 lg:py-24" data-testid="programs-hero">
        {/* Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Government Certified<br />
              <span className="text-blue-200">Skill Programs for Career Growth</span>
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 max-w-2xl mx-auto mb-8">
              Join 25,000+ candidates trained and placed through our programs
            </p>

            {/* Highlights */}
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              <span className="bg-white/20 backdrop-blur text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                <Shield className="w-4 h-4" /> Govt Recognized
              </span>
              <span className="bg-white/20 backdrop-blur text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                <IndianRupee className="w-4 h-4" /> Free & Paid Programs
              </span>
              <span className="bg-white/20 backdrop-blur text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Placement Support
              </span>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8 py-6 text-lg rounded-xl shadow-lg"
                onClick={() => handleApplyClick({title: 'Program'})}
              >
                <GraduationCap className="w-5 h-5 mr-2" /> Apply Now
              </Button>
              <a href="tel:+919521484003">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white/10 font-bold px-8 py-6 text-lg rounded-xl w-full"
                >
                  <Phone className="w-5 h-5 mr-2" /> Talk to Counselor
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT STATS */}
      <section className="py-16 bg-white" data-testid="impact-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {impactStats.map((stat, index) => (
              <div 
                key={index}
                className="text-center p-6 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-100"
              >
                <div className={`w-14 h-14 bg-${stat.color}-100 rounded-xl flex items-center justify-center mx-auto mb-4`}>
                  <stat.icon className={`w-7 h-7 text-${stat.color}-600`} />
                </div>
                <p className={`text-3xl sm:text-4xl font-bold text-${stat.color}-600 mb-2`}>{stat.number}</p>
                <p className="text-slate-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMS LIST */}
      <section className="py-16 bg-slate-50" data-testid="programs-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              All Programs
            </h2>
            <p className="text-lg text-slate-600">
              Explore all our skill development and education programs
            </p>
          </div>

          {/* Program Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {allPrograms.map((program) => (
              <ProgramCard key={program.id} program={program} />
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-16 bg-white" data-testid="why-choose">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-3">
              <Shield className="w-4 h-4" /> Why Choose Us
            </span>
            <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Why Choose Our Programs
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChoose.map((item, index) => (
              <div key={index} className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 border border-slate-100 text-center hover:shadow-lg transition-all group">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAREER OUTCOMES */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="career-outcomes">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-semibold mb-3">
              <Briefcase className="w-4 h-4" /> Career Growth
            </span>
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Career Opportunities
            </h2>
            <p className="text-lg text-slate-400">Transform your skills into high-paying careers</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {careerOutcomes.map((career, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all">
                <h3 className="text-lg font-bold text-white mb-2">{career.program}</h3>
                <p className="text-slate-400 text-sm mb-4">{career.role}</p>
                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <span className="text-slate-500 text-sm">Salary Range</span>
                  <span className="text-emerald-400 font-bold text-sm">{career.salary}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST SECTION */}
      <section className="py-16 bg-slate-50" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Recognized & Trusted Training Partner</h2>
            <p className="text-slate-600">Affiliated with leading government organizations</p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60 hover:opacity-100 transition-opacity">
            {['Skill India', 'NSDC', 'MSDE', 'ESSCI', 'TSSC'].map((org, index) => (
              <div key={index} className="bg-white rounded-xl px-8 py-4 shadow-sm border border-slate-200">
                <span className="text-slate-700 font-bold text-lg">{org}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 relative overflow-hidden" data-testid="final-cta">
        {/* Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Start Your Career Journey?
          </h2>
          <p className="text-xl text-blue-100 mb-4">
            Join India's largest skill development network
          </p>
          <p className="text-blue-200 mb-10 flex items-center justify-center gap-2">
            <Sparkles className="w-5 h-5" /> Limited seats available in ongoing batches
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8 py-6 text-lg rounded-xl shadow-lg"
              onClick={() => handleApplyClick({title: 'Skill Program'})}
            >
              <GraduationCap className="w-5 h-5 mr-2" /> Apply Now
            </Button>
            <a href="https://wa.me/919521484003?text=Hi, I want to know about skill programs" target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg" 
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg w-full"
              >
                <MessageCircle className="w-5 h-5 mr-2" /> WhatsApp Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Sticky Apply Button - Mobile */}
      <div className="fixed bottom-4 left-4 right-4 z-50 lg:hidden">
        <Button 
          className="w-full bg-blue-600 hover:bg-blue-700 py-4 text-lg font-bold rounded-xl shadow-lg"
          onClick={() => handleApplyClick({title: 'Skill Program'})}
        >
          <GraduationCap className="w-5 h-5 mr-2" /> Apply Now
        </Button>
      </div>

      <Footer />
      <Chatbot />
    </div>
  );
};

export default ProgramsPage;
